import { action, computed, observable, runInAction } from "mobx";
import { compact, omit, trim } from "lodash";
import { FormErrors, FormStore } from "@modules/formx";
import {
  AccommodationRequestBookingGroupDataFragment,
  AccommodationRequest_getBookingGroup,
  AccommodationRequest_getLast,
  AccommodationRequest_GetLastItemFragment,
  CostCenter,
  AccommodationRequest_GetAllowedCostCenters,
  HospitalityPaidBy,
  AccommodationTypeOf
} from "@graphql";
import { Navigation } from "@navigation";
import locale from "@bonliva-crm/locale";
import { CustomerType } from "@bonliva-crm/shared";
import { AccommodationData, AccommodationRequest } from "./AccommodationRequest";
import { AccommodationRequestProps } from "./AccommodationRequestProps";
import { AccommodationRequestSubmitter } from "./AccommodationRequestSubmitter";
import { durationsToWeeks, durationToWeeks, weeksToDurations } from "@bonliva-crm/common";
import { eachWeekOfInterval, getISOWeek } from "date-fns";
import { store } from "@store";
import { ProjectSelectorStore } from "@bookings/ProjectSelector";
import moment from "moment";
import {
  SwedishNumberFormatter,
  AccommodationPriceCalculator,
  AccommodationPriceType,
  TicketDifficultyLevel
} from "@bonliva-crm/shared";
import { ConsultantSelectorValue } from "@containers/VirtualSelector/ConsultantSelector/ConsultantSelector";
import { FileUploadFieldValue } from "./FileUploadField";

const weekDays = ["sön", "mån", "tis", "ons", "tors", "fre", "lör"];

export class AccommodationRequestStore {
  @observable loading = true;
  @observable isLookingUpCustomerAddress = false;
  @observable bookingGroupData: AccommodationRequestBookingGroupDataFragment;
  @observable isGotlandRegionOrg = false;
  @observable sameAsLastTime = false;
  @observable costCenters: CostCenter[] = [];
  @observable lastAccommodationRequest: AccommodationRequest_GetLastItemFragment | null =
    null;

  form: FormStore<AccommodationRequest>;

  projectSelectorStore = new ProjectSelectorStore((id) =>
    this.form.setValue("projectId", id)
  );

  isBookingGroupContext: boolean;

  static REGION_GOTLAND_ORG_ID = "5c5fd50af7ffb72545776852";

  static getIsGotlandRegionOrg(
    hospital: AccommodationRequestBookingGroupDataFragment["hospital"]
  ) {
    return Boolean(
      hospital.Parents.some(
        (parent) =>
          parent.Type === CustomerType.Region &&
          parent.Id === AccommodationRequestStore.REGION_GOTLAND_ORG_ID
      )
    );
  }

  constructor(
    public bookingGroupId: string | null,
    public props: AccommodationRequestProps,
    public nav: Navigation
  ) {
    this.isBookingGroupContext = !!bookingGroupId;

    if (this.isBookingGroupContext) {
      this.fetchData().then(() => {
        // Validate city before initializing form
        if (!this.validateCityBeforeInit()) {
          store().snackbars.error({
            text: locale.accommodationRequest.cityValidationError,
            duration: 10000
          });
          nav.close();
          return;
        }
        this.getLastAccommodationRequest();
        this.initForm();
        this.loading = false;
      });
    } else {
      this.initForm();
      this.loading = false;
    }
  }

  // New method to validate city before form initialization
  validateCityBeforeInit = (): boolean => {
    if (!this.bookingGroupData) {
      return true; // Allow if no booking group data
    }

    const { department, hospital } = this.bookingGroupData;

    // Check Department first, then Hospital
    const departmentCity = department?.Address?.City;
    const hospitalCity = hospital?.Address?.City;

    return !!(departmentCity || hospitalCity);
  };

  fetchData = () => {
    return AccommodationRequest_getBookingGroup.func(
      { id: this.bookingGroupId },
      { fetchPolicy: "network-only" }
    ).then(({ data }) => {
      runInAction(() => {
        this.bookingGroupData = data.getBookingGroup;
        const hospital = data.getBookingGroup?.hospital;
        this.isGotlandRegionOrg = hospital
          ? AccommodationRequestStore.getIsGotlandRegionOrg(hospital)
          : false;
      });
    });
  };

  getLastAccommodationRequest = () => {
    const city =
      this.form?.values?.city || this.bookingGroupData?.hospital?.Address?.City;
    const consultantId =
      this.form?.values?.consultantId ||
      this.bookingGroupData?.consultant?._id?.toString();
    if (!this.props.isNew || !city || !consultantId) {
      return;
    }

    return AccommodationRequest_getLast.func(
      {
        filter: {
          consultantIds: [consultantId],
          cityText: city
        }
      },
      { fetchPolicy: "network-only" }
    ).then(({ data }) => {
      this.lastAccommodationRequest = data.getLastAccommodationRequest;
    });
  };

  _validate = () => {
    const errors: FormErrors<AccommodationRequest> = {};

    if (!this.form) {
      return errors;
    }

    const values = this.form.values;

    // Existing zzNumber validation
    const fieldName = "zzNumber";
    if (this.isGotlandRegionOrg && !values[fieldName]) {
      errors[fieldName] = locale.accommodationRequest[fieldName];
    }

    // Budget validation - minimum 100
    if (!values.budget) {
      errors.budget = locale.accommodationRequest.budget;
    } else if (parseFloat(values.budget) < 100) {
      errors.budget = locale.accommodationRequest.minValueError(100);
    }

    if (!values.budgetPeriod) {
      errors.budgetPeriod = locale.accommodationRequest.budgetPeriod;
    }

    // Car and parking validation - conditional logic chain
    if (!values.hasOwnCar) {
      errors.hasOwnCar = locale.accommodationRequest.hasOwnCar;
    }

    if (values.hasOwnCar === "true" && !values.hasCarAccess) {
      errors.hasCarAccess = locale.accommodationRequest.hasCarAccess;
    }

    if (values.hasOwnCar === "true" && values.hasCarAccess === "true") {
      if (!values.parkingMaxPrice) {
        errors.parkingMaxPrice = locale.accommodationRequest.parkingMaxPrice;
      } else if (parseFloat(values.parkingMaxPrice) < 10) {
        errors.parkingMaxPrice = locale.accommodationRequest.minValueError(10);
      }
    }

    if (values.hasAccommodationSuggestion) {
      if (values.staffAccommodation === null) {
        errors.staffAccommodation = locale.accommodationRequest.staffAccommodation;
      }

      if (!values.bookingInformation) {
        errors.bookingInformation = locale.accommodationRequest.bookingInformation;
      }

      // Validate booking confirmation file when file upload section is shown
      // if (this.shouldShowFileUploadSection && !values.bookingConfirmationFile) {
      //   errors.bookingConfirmationFile = locale.Confirmation;
      // }
    }

    // Accepted difference validation - ensure it's not negative
    if (!values.acceptedOveragePercent) {
      errors.acceptedOveragePercent = locale.accommodationRequest.acceptedOveragePercent;
    } else if (parseFloat(values.acceptedOveragePercent) < 0) {
      errors.acceptedOveragePercent = locale.accommodationRequest.minValueError(0);
    }

    // Walking distance validation - minimum 1000
    if (values.walkingDistance && parseFloat(values.walkingDistance) < 1000) {
      errors.walkingDistance = locale.accommodationRequest.minValueError(1000);
    }

    // Reschedule validation - comment is required when reschedule is checked
    if (values.isReschedule && !values.rescheduleComment?.trim()) {
      errors.rescheduleComment = locale.accommodationRequest.rescheduleComment;
    }

    if (!values.ticketDifficultyLevel) {
      errors.ticketDifficultyLevel = locale.ticketDifficultyLevel.title;
    }

    if (this.isBookingGroupContext) {
      // Conditional validation for different city
      if (values.differentCity && !values.specifyCity) {
        errors.specifyCity = locale.accommodationRequest.specifyCity;
      }
    } else {
      if (!values.consultantId) {
        errors.consultantId = locale.Consultants;
      }

      if (!values.projectId) {
        errors.projectId = locale.Project;
      }

      if (this.isBookingContext && !values.customerAddress) {
        errors.customerAddress = locale.accommodationRequest.customerAddress;
      }

      if (!this.isBookingContext && !values.costCenterId) {
        errors.costCenterId = locale.CostCenter;
      }
    }

    return errors;
  };

  _onSetValue = (options) => {
    const { name, value, prevValue } = options;

    if (options.name === "repeat") {
      if (options.value === "custom") {
        this.form.setValue("weeks", []);
        this.form.setValue("weekDays", []);
        this.form.setValue("periods", [new AccommodationData()]);
      } else if (options.value === "weekly") {
        this.weeklyRepeat();
      }
    }

    if (options.name === "weekDays" && this.form.values.repeat === "weekly") {
      this.weeklyRepeat();
    }

    if (
      options.name.match(/periods.\d+.start/gm) ||
      options.name.match(/periods.\d+.end/gm)
    ) {
      this.updateWeeksCalendar();
    }

    if (name === "hasAccommodationSuggestion" && !value) {
      this.form.setValue("staffAccommodation", "");
      this.form.setValue("bookingConfirmationFile", null);
      this.form.setValue("accommodationLink", "");
      this.form.setValue("bookingInformation", "");
    }

    if (name === "staffAccommodation" && value === "false") {
      this.form.setValue("bookingConfirmationFile", null);
    }

    // Clear specifyCity field when differentCity is unchecked
    if (name === "differentCity" && !value) {
      this.form.setValue("specifyCity", "");
    }

    // Clear hasCarAccess and parkingMaxPrice when hasOwnCar is set to false
    if (name === "hasOwnCar" && value === "false") {
      this.form.setValue("hasCarAccess", null);
      this.form.setValue("parkingMaxPrice", null);
    }

    // Clear parkingMaxPrice when hasCarAccess is set to false
    if (name === "hasCarAccess" && value === "false") {
      this.form.setValue("parkingMaxPrice", null);
    }
    if (
      (name === "city" && value !== prevValue) ||
      (name === "consultantId" && value !== prevValue)
    ) {
      this.getLastAccommodationRequest();
    }
  };

  handleBookingGroupFormFields = (
    initialFormValues: AccommodationRequest,
    isNew: boolean
  ) => {
    if (this.bookingGroupData) {
      const { consultant, department, hospital } = this.bookingGroupData;

      if (isNew && consultant?.AccommodationPreferences) {
        initialFormValues.accommodationPreferences = consultant.AccommodationPreferences;
      }

      // Get city from Department first, then Hospital
      const departmentCity = department?.Address?.City;
      const hospitalCity = hospital?.Address?.City;
      initialFormValues.city = departmentCity || hospitalCity || "";

      const { City, StreetName, ZipCode } = department?.Address || hospital.Address || {};

      initialFormValues.customerAddress = compact(
        [City, StreetName, ZipCode].map(trim)
      ).join(", ");

      // Email for missing address is now handled in the backend
    }
  };

  initForm = () => {
    const { initialValues, isNew, isDisabled } = this.props;

    this.getCostCentersData();

    const initialFormValues = AccommodationRequest.Create(
      initialValues,
      this.bookingGroupData
    );

    this.handleBookingGroupFormFields(initialFormValues, isNew);

    this.form = new FormStore<AccommodationRequest>({
      initialValues: initialFormValues,
      initialDisabled: isDisabled,
      exposeAs: "accommodationRequestForm",
      classValidatorClassType: AccommodationRequest,
      onSetValue: this._onSetValue,
      validate: this._validate,
      onSubmit: this.submit
    });

    this.form.validate();
  };

  @action handleSameAsLastTime = () => {
    if (this.lastAccommodationRequest && !this.sameAsLastTime) {
      const excludeFields = [
        "_id",
        "staffAccommodation",
        "periods",
        "hasAccommodationSuggestion",
        "isProlongation",
        "isReschedule",
        "rescheduleComment"
      ];

      const filteredRequestData = omit(this.lastAccommodationRequest, excludeFields);

      const initValues = AccommodationRequest.FromFull({
        ...filteredRequestData,
        _id: null,
        periods: [new AccommodationData()],
        hasAccommodationSuggestion: true,
        staffAccommodation:
          this.lastAccommodationRequest.staffAccommodation !== "true" ? "false" : "true" // Reset to "false" if it was not "true"
      });

      this.form.reset(initValues);
    } else {
      const { initialValues } = this.props;
      const initialFormValues = AccommodationRequest.Create(
        initialValues,
        this.bookingGroupData
      );

      this.handleBookingGroupFormFields(initialFormValues, true);
      this.form.reset(initialFormValues);
    }
    this.form.validate();
    this.sameAsLastTime = !this.sameAsLastTime;
  };

  @action setConsultant = (consultant: ConsultantSelectorValue | null) => {
    this.form.setValue("consultantId", consultant ? consultant._id : null);

    if (consultant?.AccommodationPreferences) {
      this.form.setValue("accommodationPreferences", consultant.AccommodationPreferences);
    }
  };

  @action setCostCenter = (costCenterId: string) => {
    this.form.setValue("costCenterId", costCenterId);
  };

  @action setHasAccommodationSuggestion = (checked: boolean) => {
    this.form.setValue("hasAccommodationSuggestion", checked);
  };

  @action setIsProlongation = (checked: boolean) => {
    this.form.setValue("isProlongation", checked);
  };

  @action setIsReschedule = (checked: boolean) => {
    this.form.setValue("isReschedule", checked);

    if (!checked) {
      this.form.setValue("rescheduleComment", "");
    }
  };

  @computed get consultantValue() {
    const consultantId = this.form?.getValue("consultantId");

    return consultantId
      ? {
          label: "",
          value: consultantId
        }
      : null;
  }

  @computed get shouldShowProlongation() {
    if (!this.props.isNew) {
      // Saved requests should show prolongation field if it is checked
      return !!this.form?.values.isProlongation;
    }

    return this.sameAsLastTime;
  }

  @computed get consultantError() {
    return this.form?.getError("consultantId");
  }

  getCostCentersData = () => {
    AccommodationRequest_GetAllowedCostCenters.func(
      {},
      { fetchPolicy: "network-only" }
    ).then((data) => {
      this.costCenters = data.data.me.allowedCostCenters.filter((x) => x.IsActive);
    });
  };

  @computed get shouldShowDifferentCityField(): boolean {
    return !!this.form?.values.differentCity;
  }

  @computed get shouldShowAccommodationSuggestionSection(): boolean {
    return !!this.form?.values.hasAccommodationSuggestion;
  }

  @computed get shouldShowFileUploadSection(): boolean {
    return this.form?.values.staffAccommodation === "true";
  }

  @computed get shouldShowHasCarAccessField(): boolean {
    return this.form?.values.hasOwnCar === "true";
  }

  @computed get shouldShowParkingMaxPriceField(): boolean {
    return this.form?.values.hasCarAccess === "true";
  }

  @computed get shouldShowLegacyCarAccess(): boolean {
    return !this.props.isNew && !!this.form.values.carAccess;
  }

  @action setHasCarAccess = (value: string) => {
    this.form.setValue("hasCarAccess", value);
  };

  @action setBookingConfirmationFile = (value: FileUploadFieldValue | null) => {
    this.form.setValue("bookingConfirmationFile", value);
  };

  // Form state computed properties
  @computed get isFormDirty(): boolean {
    return !!this.form?.dirty;
  }

  @computed get canSubmitForm(): boolean {
    return !!this.form?.canSubmit;
  }

  @computed get isFormSubmitting(): boolean {
    return !!this.form?.isSubmitting;
  }

  @computed get budget(): number {
    return Number(this.form.values.budget) || 0;
  }

  @computed get acceptedOveragePercent(): number {
    return Number(this.form.values.acceptedOveragePercent) || 0;
  }

  @computed get accommodationPricePerNight() {
    return this.props.isNew
      ? AccommodationPriceCalculator.calculate(this.budget, this.acceptedOveragePercent)
      : {
          targetPerNight: this.budget,
          acceptedOveragePercent: this.acceptedOveragePercent,
          maxAllowedPerNight: this.form.values.maxAllowedPerNight
        };
  }

  @computed get shouldShowPriceInterval(): boolean {
    return this.budget > 0 && this.acceptedOveragePercent > 0;
  }

  @computed get bookingWeeks(): CustomDuration[] {
    if (this.bookingGroupData) {
      return weeksToDurations(this.bookingGroupData.bookings.map((x) => x.Week));
    }
    return [
      {
        From: moment().startOf("week").toDate(),
        To: moment().add(6, "months").startOf("week").toDate()
      }
    ];
  }

  @computed get bookingRequestedWeeks(): CustomDuration[] {
    if (this.bookingGroupData) {
      return weeksToDurations(this.bookingGroupData?.accommodationRequestedWeeks);
    }
    return [];
  }

  @computed get bookingPeriodTitle(): string {
    return this.isBookingGroupContext ? locale.BookingGroup : locale.Weeks;
  }

  @computed get showBookingPeriod(): boolean {
    return (
      this.isBookingGroupContext ||
      (!this.isBookingGroupContext && this.form.values.repeat === "weekly")
    );
  }

  @computed get isFormDisabled(): boolean {
    return !this.props.isNew;
  }

  @computed get weekDaysOptions() {
    return weekDays.map((l, i) => ({
      label: l,
      value: i - 1
    }));
  }

  @computed get repeatOptions() {
    return [
      { label: locale.RepeatWeekly, value: "weekly" },
      { label: locale.RepeatCustom, value: "custom" }
    ];
  }

  @computed get shouldShowWeekDaysSection(): boolean {
    return this.form.values.repeat === "weekly";
  }

  @computed get periodsValue() {
    return this.props.isNew ? weeksToDurations(this.form.values.weeks) : [];
  }

  @computed get isBookingContext(): boolean {
    return this.isBookingGroupContext;
  }

  @computed get paidByOptions() {
    return Object.values(HospitalityPaidBy).toLabelValue(
      (x) => locale.hospitalityPaidBy[x]
    );
  }

  @computed get accommodationTypeOptions() {
    return Object.values(AccommodationTypeOf).toLabelValue(
      (x) => locale.accommodationTypeOf[x]
    );
  }

  @computed get budgetPeriodOptions() {
    return Object.values(AccommodationPriceType).map((value) => ({
      label: locale.accommodationPeriodLabel[value],
      value
    }));
  }

  @computed get canAddPeriod(): boolean {
    return this.props.isNew;
  }

  @computed get canRemovePeriod(): boolean {
    return this.form.values.periods.length > 1 && this.props.isNew;
  }

  @computed get periodDateConstraints() {
    return this.form.values.periods.map((item, index) => {
      const start = item.start;
      const minStart = moment.utc(this.getStartDateOfBookingGroup()).subtract(1, "week");
      const maxEnd = start ? moment.utc(start) : moment().utc().add(1, "week");

      return {
        index,
        minStart: minStart.toDate(),
        maxEnd: maxEnd.toDate()
      };
    });
  }

  @computed get priceRangeDisplayValue() {
    if (!this.shouldShowPriceInterval) {
      return "";
    }

    const budget = SwedishNumberFormatter.toThousandSeparatedFormat(
      this.accommodationPricePerNight.targetPerNight
    );

    const maxAllowedPrice = SwedishNumberFormatter.toCurrencyFormat(
      this.accommodationPricePerNight.maxAllowedPerNight
    );

    return `${budget} - ${maxAllowedPrice} per ${locale.accommodationPeriodLabel[
      this.form.values.budgetPeriod
    ].toLowerCase()}.`;
  }

  @computed get shouldShowActionButtons(): boolean {
    return !this.props.isDisabled;
  }

  @computed get shouldShowRepeatSection(): boolean {
    return this.props.isNew;
  }

  @computed get shouldShowLegacySharedConveniences(): boolean {
    // Only show for existing requests (not new ones) and when there are shared conveniences
    return !this.props.isNew && !!this.form.values.sharedConveniences;
  }

  @computed get ticketDifficultyLevelError() {
    return this.form.getError("ticketDifficultyLevel");
  }

  @computed get ticketDifficultyLevelOptions() {
    return Object.values(TicketDifficultyLevel).toLabelValue(
      (x) => locale.ticketDifficultyLevel.options[x]
    );
  }

  @action setTicketDifficultyLevel = (value: TicketDifficultyLevel) => {
    this.form.setValue("ticketDifficultyLevel", value);
  };

  submit = () => {
    // calculate maxAllowedPerNight on backend side
    const { weeks, repeat, weekDays, maxAllowedPerNight, ...values } = this.form.values;
    const formValues = {
      ...values,
      zzNumber: this.isGotlandRegionOrg ? values.zzNumber : null
    };

    const periodWeeks =
      this.form.values.periods?.flatMap((x) => {
        return eachWeekOfInterval(
          { start: x.start, end: x.end },
          { weekStartsOn: 1 } // Monday
        ).map((date) => getISOWeek(date));
      }) ?? [];

    const matchBookings =
      this.bookingGroupData?.bookings.filter((x) => {
        const week = new Date(x.Week);
        const weekNumber = getISOWeek(week);

        return periodWeeks.includes(weekNumber);
      }) ?? [];

    const submitter = new AccommodationRequestSubmitter({
      bookingGroupId: this.bookingGroupId,
      matchBookings,
      accommodationRequestId: this.form.values.id,
      startDate: this.form.values.periods?.[0].start ?? new Date(),
      consultantId: this.bookingGroupData?.consultant._id,
      consultantName: this.bookingGroupData?.consultant.Name,
      location: this.form.values.city,
      tags: ["Boende"],
      onCancel: () => {
        this.form.setSubmitting(false);
        this.form.disabled = false;
      },
      formValues,
      nav: this.nav,
      setLoading: (v) => {
        this.form.disabled = v;
        this.loading = v;
      }
    });

    return submitter.submit();
  };

  addPeriod = (index: number) => {
    const periods = [...this.form.values.periods];
    periods.splice(index + 1, 0, new AccommodationData());
    this.form.setValue("periods", periods);
    this.updateWeeksCalendar();
  };

  removePeriod = (index: number) => {
    const periods = [...this.form.values.periods];
    periods.splice(index, 1);
    this.form.setValue("periods", periods);
    this.updateWeeksCalendar();
  };

  getStartDateOfBookingGroup = (): Date => {
    return new Date();
  };

  @action setWeeks = (value: CustomDuration[]) => {
    if (this.form.values.repeat === "custom") {
      return;
    }

    const newWeeks = durationsToWeeks(value, true).filter(
      (x) => !this.form.values.weeks.includes(x)
    );
    const removeWeeks = durationsToWeeks(value, true).filter((x) =>
      this.form.values.weeks.includes(x)
    );

    let result = this.form.values.weeks;

    result = result.filter((x) => removeWeeks.includes(x));
    result = [...result, ...newWeeks];

    this.form.setValue("weeks", result);
    this.weeklyRepeat();
  };

  weeklyRepeat = () => {
    const periods = [];
    const weeks = this.form.values.weeks;
    const selectedDays = this.form.values.weekDays.sort((a, b) => a - b);

    if (selectedDays.length === 0) {
      this.form.setValue("periods", periods);
      return;
    }

    if (selectedDays.length === 7) {
      let currentWeek: Date[] = [];
      const result: Date[][] = [];

      for (let i = 0; i < weeks.length; i++) {
        const week = weeks[i];

        if (
          currentWeek.length === 0 ||
          this.isNextWeek(currentWeek[currentWeek.length - 1], week)
        ) {
          currentWeek.push(week);
        } else {
          result.push(currentWeek);
          currentWeek = [week];
        }
      }

      if (currentWeek.length > 0) {
        result.push(currentWeek);
      }

      for (const week of result) {
        const start = new Date(week[0]);
        const end = new Date(week[week.length - 1]);
        start.setDate(start.getDate() - 1);
        end.setDate(end.getDate() + 5);
        periods.push({ start: start, end: end });
      }
    } else {
      weeks.forEach((week) => {
        let currentGroup: number[] = [];
        const result: number[][] = [];

        for (const day of selectedDays) {
          if (
            currentGroup.length === 0 ||
            day === currentGroup[currentGroup.length - 1] + 1
          ) {
            currentGroup.push(day);
          } else {
            result.push(currentGroup);
            currentGroup = [day];
          }
        }

        if (currentGroup.length > 0) {
          result.push(currentGroup);
        }

        for (const days of result) {
          const start = new Date(week);
          start.setDate(start.getDate() + days[0]);

          const end = new Date(week);
          end.setDate(end.getDate() + days[days.length - 1]);
          periods.push({ start: start, end: end });
        }
      });
    }

    this.form.setValue("periods", periods);
  };

  updateWeeksCalendar = () => {
    this.form.setValue(
      "weeks",
      this.form.values.periods.flatMap((x) =>
        durationToWeeks(x.start ? x.start : x.end, x.end ? x.end : x.start)
      )
    );
  };

  isNextWeek = (date1: Date, date2: Date): boolean => {
    const oneWeekInMillis = 7 * 24 * 60 * 60 * 1000;
    return Math.abs(date1.getTime() - date2.getTime()) === oneWeekInMillis;
  };
}

export const acceptedOveragePercentOptions = [10, 20, 30, 40, 50, 60, 70].map((x) => ({
  label: `${x} %`,
  value: x.toString()
}));
